import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import { formatKpi, statusOf, thresholdText, trendIsGood } from "@/lib/kpi";
import { Sparkline } from "@/components/kpi/Sparkline";

/** A status chip: icon + word, never colour alone. */
export function StatusChip({ status, className = "" }) {
  const s = statusOf(status);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${s.chip} ${className}`} data-testid={`status-${status}`}>
      <s.icon className="h-3 w-3" aria-hidden="true" />{s.label}
    </span>
  );
}

function Trend({ row }) {
  if (!row.trend) return <span className="text-xs text-muted-foreground">No earlier run</span>;
  const good = trendIsGood(row);
  const Icon = row.trend === "UP" ? ArrowUpRight : row.trend === "DOWN" ? ArrowDownRight : ArrowRight;
  const delta = row.delta === null || row.delta === undefined ? "" : `${row.delta > 0 ? "+" : ""}${formatKpi(row.delta, row.unit)}`;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={good === null ? "No change" : good ? "Better than last run" : "Worse than last run"}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />{delta || "No change"}<span className="sr-only">{good === null ? "" : good ? " (better)" : " (worse)"}</span>
      {good !== null && <span className="text-[11px]">{good ? "better" : "worse"}</span>}
    </span>
  );
}

/** One KPI: value, status, trend against the last run, the last runs as a sparkline, and a link to the documents behind it. */
export function KpiCard({ row, showScope = true }) {
  const s = statusOf(row.status);
  const scope = row.project_code ? `${row.project_code}${row.project_name ? ` · ${row.project_name}` : ""}` : row.company_name;
  return (
    <Link to={row.link || "#"} data-testid={`kpi-card-${row.code}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#910029]/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#910029]/40 dark:border-slate-700 dark:bg-slate-900">
      <span className={`absolute inset-y-0 left-0 w-1 ${s.bar}`} aria-hidden="true" />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{row.name}</div>
          {showScope && scope && <div className="truncate text-xs text-muted-foreground">{scope}</div>}
        </div>
        <StatusChip status={row.status} />
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-50">{formatKpi(row.value, row.unit)}</div>
          <Trend row={row} />
        </div>
        {row.history && row.history.length > 1 && <Sparkline points={row.history} unit={row.unit} label={row.name} />}
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">{thresholdText(row)}</div>
      <div className="mt-1 text-[11px] font-medium text-[#910029] opacity-0 transition group-hover:opacity-100 dark:text-rose-300">Open the documents →</div>
    </Link>
  );
}
